/**
 * Recently viewed: storage (localStorage) + cart slider component.
 * - RecentlyViewed: addProduct / getProducts / getEntries (used on product page, section, predictive search).
 * - <cart-recently-viewed>: cart block that fetches product cards and inits Swiper.
 */
import { ThemeEvents } from '@theme/events';

export class RecentlyViewed {
  static #STORAGE_KEY = 'viewedProducts';
  static #MAX_PRODUCTS = 10;

  static addProduct(productId, variantId = '') {
    let entries = this.getEntries();
    entries = entries.filter((e) => e.product_id !== productId);
    entries.unshift({ product_id: productId, variant_id: variantId || '' });
    entries = entries.slice(0, this.#MAX_PRODUCTS);
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(entries));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  static getEntries() {
    const raw = localStorage.getItem(this.#STORAGE_KEY) || '[]';
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((e) => ({
          product_id: String(e?.product_id ?? e ?? ''),
          variant_id: String(e?.variant_id ?? ''),
        }));
      }
    } catch (_) {
      try {
        const legacy = JSON.parse(raw);
        if (Array.isArray(legacy)) {
          return legacy.map((id) => ({ product_id: String(id), variant_id: '' }));
        }
      } catch (_2) {
        // ignore
      }
    }
    return [];
  }

  static getProducts() {
    const ids = this.getEntries().map((e) => e.product_id).filter(Boolean);
    return [...new Set(ids)];
  }
}

const SEARCH_BASE = typeof window.Theme?.routes?.search_url === 'string'
  ? window.Theme.routes.search_url
  : '/search';

class CartRecentlyViewed extends HTMLElement {
  #abort = null;
  #swiper = null;
  #observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0]?.isIntersecting) return;
      this.#observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 200px 0px' }
  );

  #onCartUpdate = () => {
    const wrap = this.querySelector('.cart-recently-viewed__swiper-wrap');
    if (wrap?.getAttribute('data-has-products') === 'true') {
      if (!this.#swiper && wrap.querySelector('.swiper-wrapper')?.children?.length) {
        requestAnimationFrame(() => this.#initSwiper());
      }
      return;
    }
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#load());
    });
  };

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
    if (this.getBoundingClientRect().top < window.innerHeight + 200) {
      setTimeout(() => this.#load(), 100);
    } else {
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
    this.#observer.disconnect();
    this.#abort?.abort();
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #initSwiper() {
    const wrap = this.querySelector('.cart-recently-viewed__swiper-wrap');
    const el = this.querySelector('.cart-recently-viewed__swiper');
    if (!wrap || !el || wrap.getAttribute('data-has-products') !== 'true') return;
    if (!el.querySelector('.swiper-wrapper')?.children?.length) return;
    if (typeof window.Swiper !== 'function') {
      requestAnimationFrame(() => this.#initSwiper());
      return;
    }
    this.#swiper?.destroy(true, true);
    const prev = wrap.querySelector('.cart-recently-viewed__arrow--prev');
    const next = wrap.querySelector('.cart-recently-viewed__arrow--next');
    const pagination = wrap.querySelector('.cart-recently-viewed__pagination');
    this.#swiper = new window.Swiper(el, {
      slidesPerView: 2,
      slidesPerGroup: 1,
      spaceBetween: 20,
      loop: false,
      breakpoints: {
        1200: { slidesPerView: 3, spaceBetween: 20 },
      },
      navigation: this.dataset.showArrows === 'true' && prev && next ? { prevEl: prev, nextEl: next } : false,
      pagination: this.dataset.showPagination === 'true' && pagination ? { el: pagination, clickable: true } : false,
      observer: true,
      observeParents: true,
      on: {
        init(swiper) {
          el.classList.add('swiper-initialized');
          const n = swiper.slides?.length ?? 0;
          const p = typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 4;
          if (n <= p) wrap.classList.add('cart-recently-viewed__swiper-wrap--no-nav');
        },
      },
    });
  }

  async #load() {
    const wrap = this.querySelector('.cart-recently-viewed__swiper-wrap');
    if (wrap?.getAttribute('data-has-products') === 'true') return;
    const setLoadingFalse = () => wrap?.setAttribute('data-loading', 'false');
    if (!RecentlyViewed?.getProducts) {
      setLoadingFalse();
      this.style.display = 'none';
      return;
    }
    const ids = RecentlyViewed.getProducts().slice(0, parseInt(this.dataset.maxProducts || '6', 10));
    if (!ids.length) {
      setLoadingFalse();
      this.style.display = 'none';
      return;
    }
    const url = new URL(SEARCH_BASE, window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    this.#abort = new AbortController();
    let html;
    try {
      html = await (await fetch(url.toString(), { signal: this.#abort.signal })).text();
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Cart recently viewed:', e);
      setLoadingFalse();
      this.style.display = 'none';
      return;
    } finally {
      this.#abort = null;
    }
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const allItems = Array.from(doc.querySelectorAll('.product-grid__item'));
    const idSet = new Set(ids.map(String));
    const byId = new Map();
    allItems.forEach((el) => {
      const pid = el.getAttribute('data-product-id');
      if (pid && idSet.has(pid)) byId.set(pid, el);
    });
    const items = ids.map((id) => byId.get(String(id))).filter(Boolean);
    if (!items.length) {
      setLoadingFalse();
      this.style.display = 'none';
      return;
    }
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper || !wrap) {
      setLoadingFalse();
      return;
    }
    wrapper.innerHTML = '';
    items.forEach((item) => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      const inner = document.createElement('div');
      inner.className = 'cart-recently-viewed__slide-inner';
      inner.innerHTML = item.innerHTML;

      // Execute any inline Doran BIS init scripts that were copied with the card HTML
      if(inner){
      inner.querySelectorAll('script').forEach((scriptEl) => {
        const code = scriptEl.textContent || '';
        if (code && code.indexOf('window.$bpDoranInit') !== -1) {
          try {
            // Run the inline init script in the current window context
            // eslint-disable-next-line no-new-func
            new Function(code)();
          } catch (e) {
            console.error('Cart recently viewed: Doran BIS init failed', e);
          }
        }
        // Remove script tags from the DOM to avoid duplicate execution
        scriptEl.remove();
      });
      }

      slide.appendChild(inner);
      wrapper.appendChild(slide);
    });

    // Re-initialize Smart Wishlist buttons for newly injected product cards
    if (typeof window.ReloadSmartWishlist === 'function') {
      try {
        window.ReloadSmartWishlist();
      } catch (e) {
        console.error('Cart recently viewed: ReloadSmartWishlist failed', e);
      }
    }

    wrap.setAttribute('data-has-products', 'true');
    wrap.setAttribute('data-loading', 'false');
    const init = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (wrap.offsetWidth > 0) this.#initSwiper();
        });
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(init, 0));
    } else {
      setTimeout(init, 0);
    }
  }
}

if (!customElements.get('cart-recently-viewed')) {
  customElements.define('cart-recently-viewed', CartRecentlyViewed);
}
